import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { reportAPI } from '../../services/api';
import toast from 'react-hot-toast';

const Reports = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [visitData, setVisitData] = useState(null);
  const [salesData, setSalesData] = useState(null);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    regionId: '',
    visitTypeId: '',
    vendorId: '',
    productId: ''
  });

  useEffect(() => {
    fetchReports();
  }, [filters]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const [visitsRes, salesRes] = await Promise.all([
        reportAPI.getVisitsReport(filters),
        reportAPI.getSalesReport(filters)
      ]);
      setVisitData(visitsRes.data.data);
      setSalesData(salesRes.data.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const formatMoney = (amount) => {
    return '₹' + Number(amount).toLocaleString('en-IN');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Reports</h1>
        <p className="text-gray-600 mt-1">Performance across products, regions and the field force.</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <input
            type="date"
            value={filters.dateFrom}
            onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            placeholder="From"
          />
          <input
            type="date"
            value={filters.dateTo}
            onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            placeholder="To"
          />
          <button
            onClick={() => setFilters({
              dateFrom: '',
              dateTo: '',
              regionId: '',
              visitTypeId: '',
              vendorId: '',
              productId: ''
            })}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Visit Summary */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Visit Summary</h2>
        {visitData?.summary ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Total Visits</p>
              <p className="text-2xl font-bold text-blue-600">{visitData.summary.totalVisits || 0}</p>
            </div>
            {Object.entries(visitData.summary.byType || {}).map(([type, count]) => (
              <div key={type} className="bg-purple-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">{type} Visits</p>
                <p className="text-2xl font-bold text-purple-600">{count}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500">No visit data available</p>
        )}
      </div>

      {/* Sales Summary */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Sales Summary</h2>
        {salesData?.summary ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">Total Revenue</p>
                <p className="text-2xl font-bold text-green-600">{formatMoney(salesData.summary.totalRevenue || 0)}</p>
              </div>
              <div className="bg-indigo-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">Total Sales</p>
                <p className="text-2xl font-bold text-indigo-600">{salesData.summary.totalSales || 0}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* By Product */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-700 mb-2">Sales by Product</h3>
                {Object.entries(salesData.summary.byProduct || {}).map(([name, amount]) => (
                  <div key={name} className="flex justify-between py-1 text-sm">
                    <span className="text-gray-600">{name}</span>
                    <span className="font-medium">{formatMoney(amount)}</span>
                  </div>
                ))}
              </div>

              {/* By Vendor */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-700 mb-2">Sales by Vendor</h3>
                {Object.entries(salesData.summary.byVendor || {}).map(([name, amount]) => (
                  <div key={name} className="flex justify-between py-1 text-sm">
                    <span className="text-gray-600">{name}</span>
                    <span className="font-medium">{formatMoney(amount)}</span>
                  </div>
                ))}
              </div>

              {/* By Executive */}
              {user?.role === 'admin' && (
                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-700 mb-2">Sales by Executive</h3>
                  {Object.entries(salesData.summary.byExecutive || {}).map(([name, amount]) => (
                    <div key={name} className="flex justify-between py-1 text-sm">
                      <span className="text-gray-600">{name}</span>
                      <span className="font-medium">{formatMoney(amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          <p className="text-gray-500">No sales data available</p>
        )}
      </div>
    </div>
  );
};

export default Reports;